import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { auth } from '../config/firebase';
import { adminUpdateMandantRoot } from '../config/adminOverwriteGuard';
import useAdminStore from '../store/adminStore';
import {
    resolveStripeBillingBadge,
    STRIPE_PAYMENT_LOCK_ARMED,
    type StripeBillingBadge,
} from '../utils/stripeBilling';
import { formatMandantNrDisplay } from '../utils/mandantNrFormat';

const IconBase = ({ children, size = 20, className = '' }: any) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
    >
        {children}
    </svg>
);
const AlertTriangleIcon = (props: any) => (
    <IconBase {...props}>
        <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
    </IconBase>
);
const XIcon = (props: any) => (
    <IconBase {...props}>
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
    </IconBase>
);
const ExternalLinkIcon = (props: any) => (
    <IconBase {...props}>
        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
        <polyline points="15 3 21 3 21 9" />
        <line x1="10" y1="14" x2="21" y2="3" />
    </IconBase>
);
const CheckCircleIcon = (props: any) => (
    <IconBase {...props}>
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
        <path d="m9 11 3 3L22 4" />
    </IconBase>
);
const CreditCardIcon = (props: any) => (
    <IconBase {...props}>
        <rect width="22" height="16" x="1" y="4" rx="2" ry="2" />
        <line x1="1" y1="10" x2="23" y2="10" />
    </IconBase>
);
const BellOffIcon = (props: any) => (
    <IconBase {...props}>
        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        <path d="M18.63 13A17.89 17.89 0 0 1 18 8" />
        <path d="M6.26 6.26A5.86 5.86 0 0 0 6 8c0 7-3 9-3 9h14" />
        <path d="M18 8a6 6 0 0 0-9.33-5" />
        <line x1="1" y1="1" x2="23" y2="23" />
    </IconBase>
);
const ClockIcon = (props: any) => (
    <IconBase {...props}>
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
    </IconBase>
);
const UnlockIcon = (props: any) => (
    <IconBase {...props}>
        <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
        <path d="M7 11V7a5 5 0 0 1 9.9-1" />
    </IconBase>
);
const ChevronDownIcon = (props: any) => (
    <IconBase {...props}>
        <polyline points="6 9 12 15 18 9" />
    </IconBase>
);
const ChevronUpIcon = (props: any) => (
    <IconBase {...props}>
        <polyline points="18 15 12 9 6 15" />
    </IconBase>
);

type StripeRadarAction = 'snooze' | 'mute' | 'unmute' | 'unsnooze' | 'bypass' | 'clear_bypass';

const readFlag = (client: any, key: string): boolean =>
    client?.[key] === true || client?.clientData?.[key] === true;

const readNum = (client: any, key: string): number => {
    const n = Number(client?.[key] ?? client?.clientData?.[key] ?? 0);
    return Number.isFinite(n) ? n : 0;
};

const resolveActor = (adminTeam: Array<{ name?: string; email?: string }>) => {
    const user = auth.currentUser;
    const email = (user?.email || '').trim().toLowerCase();
    const fromTeam = adminTeam.find((m) => (m.email || '').trim().toLowerCase() === email);
    const name =
        (fromTeam?.name || '').trim() ||
        (user?.displayName || '').trim() ||
        email ||
        'Unbekannt';
    return { email: email || 'unbekannt', name, uid: user?.uid || '' };
};

const formatAt = (raw: unknown): string => {
    const n = typeof raw === 'number' ? raw : Number(raw);
    if (!Number.isFinite(n) || n <= 0) return '—';
    try {
        return new Date(n).toLocaleString('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    } catch {
        return '—';
    }
};

type AlertRow = {
    client: any;
    clientName: string;
    badge: StripeBillingBadge;
    issues: string[];
};

/**
 * Stripe Radar — Frühwarnung für fehlgeschlagene / rückgebuchte Zahlungen.
 * Stil wie Sentinel; Zugangssperre bleibt über STRIPE_PAYMENT_LOCK_ARMED gesteuert.
 */
const StripeRadar: React.FC = () => {
    const { crmClients, pendingApprovals, startImpersonation, adminTeam, fetchClients } =
        useAdminStore();

    const [isOpen, setIsOpen] = useState(false);
    const [expandedClient, setExpandedClient] = useState<string | null>(null);
    const [sessionDismissed, setSessionDismissed] = useState<string[]>([]);
    const [isProcessing, setIsProcessing] = useState(false);
    const [snoozeValues, setSnoozeValues] = useState<Record<string, number>>({});

    const allClients = useMemo(
        () => [...(crmClients || []), ...(pendingApprovals || [])],
        [crmClients, pendingApprovals]
    );

    const radarAlerts = useMemo(() => {
        const now = Date.now();
        const alerts: AlertRow[] = [];

        allClients.forEach((client) => {
            if (!client?.id || client.isArchived || client.isDeleted) return;
            if (sessionDismissed.includes(client.id)) return;
            if (readFlag(client, 'stripeRadarMuted')) return;
            const snoozedUntil = readNum(client, 'stripeRadarSnoozedUntil');
            if (snoozedUntil > now) return;

            const years = client.geschaeftsjahre || [];
            const latestStatus =
                years.length > 0 ? years[years.length - 1].status : 'datenerfassung';
            const badge = resolveStripeBillingBadge(client, { latestYearStatus: latestStatus });
            if (!badge.isCritical) return;

            const clientName =
                `${client.vorname || ''} ${client.nachname || ''}`.trim() ||
                client.firmenname ||
                client.clientData?.salonname ||
                'Unbekannter Mandant';

            const issues: string[] = [];
            issues.push(badge.display);
            if (badge.label === 'Rückbuchung') {
                issues.push('Rückbuchung / Dispute — sofort Kontakt aufnehmen.');
            } else if (badge.label === 'Nicht abgebucht') {
                issues.push('Abbuchung fehlgeschlagen oder überfällig (past_due / unpaid).');
            } else {
                issues.push('Kritischer Stripe-Status — Zahlung prüfen.');
            }
            if (readFlag(client, 'stripePaymentLockBypass')) {
                issues.push('Admin-Bypass aktiv — Zugangssperre ausgesetzt.');
            } else if (STRIPE_PAYMENT_LOCK_ARMED) {
                issues.push('Zugang ist gesperrt (Zahlungsstop scharf).');
            } else {
                issues.push('Sperre vorbereitet, noch nicht scharfgeschaltet.');
            }

            alerts.push({ client, clientName, badge, issues });
        });

        alerts.sort((a, b) => a.clientName.localeCompare(b.clientName, 'de'));
        return alerts;
    }, [allClients, sessionDismissed]);

    const restingEntries = useMemo(() => {
        const now = Date.now();
        const rows: Array<{
            client: any;
            clientName: string;
            kind: 'mute' | 'snooze';
            snoozedUntil: number | null;
            byName: string;
            at: number;
        }> = [];

        allClients.forEach((client) => {
            if (!client?.id || client.isArchived || client.isDeleted) return;
            const muted = readFlag(client, 'stripeRadarMuted');
            const snoozedUntil = readNum(client, 'stripeRadarSnoozedUntil');
            const snoozeActive = snoozedUntil > now;
            if (!muted && !snoozeActive) return;

            const clientName =
                `${client.vorname || ''} ${client.nachname || ''}`.trim() ||
                'Unbekannter Mandant';
            rows.push({
                client,
                clientName,
                kind: muted ? 'mute' : 'snooze',
                snoozedUntil: snoozeActive ? snoozedUntil : null,
                byName: String(
                    client.stripeRadarLastActionByName ||
                        client.clientData?.stripeRadarLastActionByName ||
                        ''
                ),
                at: Number(
                    client.stripeRadarLastActionAt ||
                        client.clientData?.stripeRadarLastActionAt ||
                        0
                ),
            });
        });

        rows.sort((a, b) => b.at - a.at);
        return rows;
    }, [allClients]);

    const buildAuditPayload = (action: StripeRadarAction, extra: Record<string, unknown> = {}) => {
        const actor = resolveActor(adminTeam || []);
        const at = Date.now();
        return {
            stripeRadarLastAction: action,
            stripeRadarLastActionAt: at,
            stripeRadarLastActionByEmail: actor.email,
            stripeRadarLastActionByName: actor.name,
            stripeRadarLastActionByUid: actor.uid,
            'clientData.stripeRadarLastAction': action,
            'clientData.stripeRadarLastActionAt': at,
            'clientData.stripeRadarLastActionByEmail': actor.email,
            'clientData.stripeRadarLastActionByName': actor.name,
            'clientData.stripeRadarLastActionByUid': actor.uid,
            ...extra,
        };
    };

    const handleSnooze = async (e: React.MouseEvent, clientId: string, days: number) => {
        e.stopPropagation();
        setIsProcessing(true);
        const until = Date.now() + days * 24 * 60 * 60 * 1000;
        try {
            await adminUpdateMandantRoot(clientId, {
                ...buildAuditPayload('snooze', {
                    stripeRadarSnoozedUntil: until,
                    'clientData.stripeRadarSnoozedUntil': until,
                }),
            });
            setSessionDismissed((prev) => [...prev, clientId]);
            if (expandedClient === clientId) setExpandedClient(null);
            await fetchClients();
        } catch (err) {
            console.error('StripeRadar snooze:', err);
        } finally {
            setIsProcessing(false);
        }
    };

    const handleMuteForever = async (e: React.MouseEvent, clientId: string, clientName: string) => {
        e.stopPropagation();
        if (
            !window.confirm(
                `Stripe-Radar für "${clientName}" dauerhaft stummschalten? Zahlungsprobleme erscheinen dann nicht mehr.`
            )
        ) {
            return;
        }
        setIsProcessing(true);
        try {
            await adminUpdateMandantRoot(clientId, {
                ...buildAuditPayload('mute', {
                    stripeRadarMuted: true,
                    'clientData.stripeRadarMuted': true,
                }),
            });
            setSessionDismissed((prev) => [...prev, clientId]);
            if (expandedClient === clientId) setExpandedClient(null);
            await fetchClients();
        } catch (err) {
            console.error('StripeRadar mute:', err);
        } finally {
            setIsProcessing(false);
        }
    };

    const handleReactivate = async (
        e: React.MouseEvent,
        clientId: string,
        clientName: string,
        kind: 'mute' | 'snooze'
    ) => {
        e.stopPropagation();
        if (!window.confirm(`Stripe-Radar für "${clientName}" wieder aktivieren?`)) return;
        setIsProcessing(true);
        try {
            await adminUpdateMandantRoot(clientId, {
                ...buildAuditPayload(kind === 'mute' ? 'unmute' : 'unsnooze', {
                    stripeRadarMuted: false,
                    'clientData.stripeRadarMuted': false,
                    stripeRadarSnoozedUntil: 0,
                    'clientData.stripeRadarSnoozedUntil': 0,
                }),
            });
            setSessionDismissed((prev) => prev.filter((id) => id !== clientId));
            await fetchClients();
        } catch (err) {
            console.error('StripeRadar reactivate:', err);
        } finally {
            setIsProcessing(false);
        }
    };

    const handleBypassToggle = async (
        e: React.MouseEvent,
        clientId: string,
        clientName: string,
        currentlyBypassed: boolean
    ) => {
        e.stopPropagation();
        const next = !currentlyBypassed;
        if (
            next &&
            !window.confirm(
                `Zugangssperre für "${clientName}" vorübergehend aussetzen (Bypass)?`
            )
        ) {
            return;
        }
        setIsProcessing(true);
        try {
            await adminUpdateMandantRoot(clientId, {
                ...buildAuditPayload(next ? 'bypass' : 'clear_bypass', {
                    stripePaymentLockBypass: next,
                    'clientData.stripePaymentLockBypass': next,
                }),
            });
            await fetchClients();
        } catch (err) {
            console.error('StripeRadar bypass:', err);
        } finally {
            setIsProcessing(false);
        }
    };

    const handleOpenAkte = (e: React.MouseEvent, clientId: string, clientName: string) => {
        e.stopPropagation();
        startImpersonation(clientId, clientName);
        setIsOpen(false);
    };

    return (
        <>
            <button
                type="button"
                onClick={() => setIsOpen(true)}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-lg border transition-all shadow-lg group ${
                    radarAlerts.length > 0
                        ? 'bg-red-50 border-red-500 text-red-800 hover:bg-red-100 dark:bg-red-900/10 dark:border-red-500/50 dark:hover:bg-red-900/20 dark:text-red-400'
                        : 'bg-pe-card border-pe-border-strong text-pe-text hover:bg-pe-hover'
                }`}
            >
                <div className="flex items-center gap-3">
                    <CreditCardIcon
                        size={18}
                        className={
                            radarAlerts.length > 0 ? 'text-red-600 dark:text-red-500 animate-pulse' : 'text-pe-muted'
                        }
                    />
                    <span
                        className={`text-sm font-bold ${
                            radarAlerts.length > 0 ? 'text-red-800 dark:text-red-400' : 'text-pe-text'
                        }`}
                    >
                        Stripe Radar
                    </span>
                </div>
                {radarAlerts.length > 0 && (
                    <span className="bg-red-500 text-white text-[10px] font-black px-2 py-0.5 rounded shadow-[0_0_10px_rgba(239,68,68,0.5)]">
                        {radarAlerts.length}
                    </span>
                )}
            </button>

            {isOpen &&
                createPortal(
                    <div className="fixed inset-0 z-[100000] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in text-left">
                        <div className="bg-[#0f1015] border border-slate-800 rounded-2xl w-full max-w-2xl shadow-[0_0_50px_rgba(0,0,0,0.8)] flex flex-col max-h-[85vh]">
                            <div className="p-6 border-b border-slate-800 flex justify-between items-center shrink-0 bg-[#15171e] rounded-t-2xl">
                                <div>
                                    <div className="text-[10px] text-red-500 font-black tracking-[0.3em] uppercase mb-1 flex items-center gap-2">
                                        <AlertTriangleIcon size={12} /> Zahlungs-Frühwarnung
                                        {!STRIPE_PAYMENT_LOCK_ARMED && (
                                            <span className="text-amber-500/80 tracking-widest">
                                                · Sperre aus
                                            </span>
                                        )}
                                    </div>
                                    <h2 className="text-xl font-bold text-white tracking-widest uppercase">
                                        Stripe Radar
                                    </h2>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setIsOpen(false)}
                                    className="p-2 bg-slate-800 hover:bg-slate-700 rounded-full text-slate-400 transition-colors"
                                >
                                    <XIcon size={20} />
                                </button>
                            </div>

                            <div className="flex-1 overflow-y-auto p-6 space-y-4">
                                {radarAlerts.length === 0 ? (
                                    <div className="text-center py-12">
                                        <CheckCircleIcon
                                            size={48}
                                            className="mx-auto text-emerald-600/50 mb-4"
                                        />
                                        <h3 className="text-slate-300 font-bold uppercase tracking-widest">
                                            Keine Zahlungsalarme
                                        </h3>
                                        <p className="text-xs text-slate-500 mt-2 max-w-sm mx-auto">
                                            Keine fehlgeschlagenen Abbuchungen oder Rückbuchungen
                                            gemeldet. (Solange Stripe nicht live ist, bleibt die
                                            Liste leer.)
                                        </p>
                                    </div>
                                ) : (
                                    radarAlerts.map(({ client, clientName, badge, issues }) => {
                                        const isExpanded = expandedClient === client.id;
                                        const bypassed = readFlag(client, 'stripePaymentLockBypass');
                                        const idLabel =
                                            formatMandantNrDisplay(
                                                client.mandantNr || client.clientData?.mandantNr
                                            ) || client.id.substring(0, 8);

                                        return (
                                            <div
                                                key={client.id}
                                                className="bg-[#15171e] border border-red-900/40 rounded-xl overflow-hidden shadow-lg"
                                            >
                                                <div
                                                    className="p-4 flex justify-between items-center cursor-pointer hover:bg-slate-800/50 transition-colors"
                                                    onClick={() =>
                                                        setExpandedClient(
                                                            isExpanded ? null : client.id
                                                        )
                                                    }
                                                >
                                                    <div className="flex items-center gap-4">
                                                        <div className="w-2 h-2 rounded-full bg-red-500 shadow-[0_0_10px_rgba(239,68,68,0.8)] animate-pulse" />
                                                        <div>
                                                            <div className="font-bold text-white text-sm">
                                                                {clientName}
                                                            </div>
                                                            <div className="text-[10px] text-slate-500 font-mono mt-1">
                                                                ID: {idLabel}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-3">
                                                        <span
                                                            className={`text-[10px] font-black px-2 py-1 rounded uppercase tracking-widest border ${badge.className}`}
                                                        >
                                                            {badge.label}
                                                        </span>
                                                        <span className="text-slate-500">
                                                            {isExpanded ? (
                                                                <ChevronUpIcon size={18} />
                                                            ) : (
                                                                <ChevronDownIcon size={18} />
                                                            )}
                                                        </span>
                                                    </div>
                                                </div>

                                                {isExpanded && (
                                                    <div className="p-4 border-t border-slate-800 bg-[#0c0d11]">
                                                        <ul className="space-y-2 mb-6">
                                                            {issues.map((issue, idx) => (
                                                                <li
                                                                    key={idx}
                                                                    className="text-sm text-slate-300 flex items-start gap-2"
                                                                >
                                                                    <span className="text-red-500 mt-1">
                                                                        •
                                                                    </span>{' '}
                                                                    {issue}
                                                                </li>
                                                            ))}
                                                        </ul>

                                                        <div className="flex flex-wrap items-center gap-3">
                                                            <button
                                                                type="button"
                                                                onClick={(e) =>
                                                                    handleOpenAkte(
                                                                        e,
                                                                        client.id,
                                                                        clientName
                                                                    )
                                                                }
                                                                className="flex-1 sm:flex-none flex items-center justify-center gap-2 bg-[#E6C299] hover:bg-white text-black px-4 py-2 rounded text-[10px] font-black uppercase tracking-widest transition-colors shadow-lg"
                                                            >
                                                                <ExternalLinkIcon size={14} /> Akte
                                                                öffnen
                                                            </button>

                                                            <button
                                                                type="button"
                                                                disabled={isProcessing}
                                                                onClick={(e) =>
                                                                    handleBypassToggle(
                                                                        e,
                                                                        client.id,
                                                                        clientName,
                                                                        bypassed
                                                                    )
                                                                }
                                                                className={`flex items-center justify-center gap-2 px-4 py-2 rounded text-[10px] font-black uppercase tracking-widest border transition-colors ${
                                                                    bypassed
                                                                        ? 'bg-amber-500/15 text-amber-300 border-amber-500/40'
                                                                        : 'bg-slate-800 text-slate-300 border-slate-700 hover:border-amber-500/40'
                                                                }`}
                                                                title="Zugangssperre für diesen Mandanten aussetzen"
                                                            >
                                                                <UnlockIcon size={14} />
                                                                {bypassed
                                                                    ? 'Bypass aktiv'
                                                                    : 'Sperre aussetzen'}
                                                            </button>

                                                            <div className="flex-1 sm:flex-none flex items-center bg-slate-800 rounded border border-slate-700 overflow-hidden">
                                                                <div className="px-3 flex items-center text-slate-400">
                                                                    <ClockIcon size={14} />
                                                                </div>
                                                                <select
                                                                    value={
                                                                        snoozeValues[client.id] || 7
                                                                    }
                                                                    onChange={(e) =>
                                                                        setSnoozeValues((prev) => ({
                                                                            ...prev,
                                                                            [client.id]: parseInt(
                                                                                e.target.value,
                                                                                10
                                                                            ),
                                                                        }))
                                                                    }
                                                                    className="bg-transparent text-slate-300 text-[10px] font-bold uppercase tracking-widest outline-none cursor-pointer py-2 pr-2 appearance-none"
                                                                    onClick={(e) =>
                                                                        e.stopPropagation()
                                                                    }
                                                                >
                                                                    <option value={3}>3 Tage</option>
                                                                    <option value={7}>7 Tage</option>
                                                                    <option value={14}>
                                                                        14 Tage
                                                                    </option>
                                                                    <option value={30}>
                                                                        30 Tage
                                                                    </option>
                                                                </select>
                                                                <button
                                                                    type="button"
                                                                    onClick={(e) =>
                                                                        handleSnooze(
                                                                            e,
                                                                            client.id,
                                                                            snoozeValues[
                                                                                client.id
                                                                            ] || 7
                                                                        )
                                                                    }
                                                                    disabled={isProcessing}
                                                                    className="bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 text-[10px] font-black uppercase tracking-widest transition-colors border-l border-slate-600"
                                                                >
                                                                    Pausieren
                                                                </button>
                                                            </div>

                                                            <button
                                                                type="button"
                                                                onClick={(e) =>
                                                                    handleMuteForever(
                                                                        e,
                                                                        client.id,
                                                                        clientName
                                                                    )
                                                                }
                                                                disabled={isProcessing}
                                                                className="w-full sm:w-auto flex items-center justify-center gap-2 bg-red-900/10 hover:bg-red-900/30 text-red-500 border border-red-900/50 hover:border-red-500 px-4 py-2 rounded text-[10px] font-black uppercase tracking-widest transition-colors sm:ml-auto"
                                                            >
                                                                <BellOffIcon size={14} /> Ignorieren
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })
                                )}

                                {restingEntries.length > 0 && (
                                    <div className="pt-4 mt-2 border-t border-slate-800">
                                        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 mb-3">
                                            Ruhend / Ignoriert ({restingEntries.length})
                                        </p>
                                        <div className="space-y-2">
                                            {restingEntries.map(
                                                ({
                                                    client,
                                                    clientName,
                                                    kind,
                                                    snoozedUntil,
                                                    byName,
                                                    at,
                                                }) => (
                                                    <div
                                                        key={`stripe-rest-${client.id}`}
                                                        className="rounded-lg border border-slate-800 bg-[#12141a] px-3 py-2.5"
                                                    >
                                                        <div className="flex flex-wrap items-center justify-between gap-2">
                                                            <p className="text-sm font-bold text-slate-200">
                                                                {clientName}
                                                            </p>
                                                            <span className="text-[10px] font-black uppercase tracking-wider text-amber-500/90">
                                                                {kind === 'mute'
                                                                    ? 'Ignoriert (dauerhaft)'
                                                                    : `Pausiert bis ${formatAt(snoozedUntil)}`}
                                                            </span>
                                                        </div>
                                                        <p className="mt-1 text-xs text-slate-500">
                                                            Von:{' '}
                                                            <span className="text-slate-300">
                                                                {byName || '—'}
                                                            </span>
                                                            {' · '}
                                                            Am:{' '}
                                                            <span className="text-slate-300">
                                                                {formatAt(at)}
                                                            </span>
                                                        </p>
                                                        <button
                                                            type="button"
                                                            disabled={isProcessing}
                                                            onClick={(e) =>
                                                                handleReactivate(
                                                                    e,
                                                                    client.id,
                                                                    clientName,
                                                                    kind
                                                                )
                                                            }
                                                            className="mt-2 text-[10px] font-black uppercase tracking-wider text-emerald-400/90 hover:text-emerald-300 border border-emerald-500/30 hover:border-emerald-400/50 rounded-md px-2.5 py-1 transition-colors disabled:opacity-40"
                                                        >
                                                            Wieder aktivieren
                                                        </button>
                                                    </div>
                                                )
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>,
                    document.body
                )}
        </>
    );
};

export default StripeRadar;
